import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Helper: read a cookie by name (returns null if missing).
function getCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

// Attach bearer if available + CSRF token on mutations.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cd_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrf = getCookie("csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

// Silent refresh: on 401 from anywhere except the auth endpoints, try
// POST /auth/refresh once, replay the request. Keeps "remember me" sessions
// alive without dumping the user back to /login every 12 hours.
let refreshingPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (
      status !== 401 ||
      !original ||
      original._retried ||
      /\/auth\/(login|refresh|logout|register)$/.test(original.url || "")
    ) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      refreshingPromise =
        refreshingPromise ||
        api.post("/auth/refresh").finally(() => {
          setTimeout(() => (refreshingPromise = null), 0);
        });
      const { data } = await refreshingPromise;
      if (data?.access_token) {
        localStorage.setItem("cd_token", data.access_token);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${data.access_token}`;
      }
      return api(original);
    } catch (e) {
      // Refresh failed → real logout
      localStorage.removeItem("cd_token");
      return Promise.reject(error);
    }
  },
);

export const fmtCurrency = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number.isFinite(n) ? n : 0,
  );

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const fmtDateShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
